import { createPatch } from 'rfc6902';
import { uuidv7 } from 'uuidv7';
import { PatientTherapySequenceStatus } from '../../database/schema.js';
import { zql } from '../../schema.js';
import { getTherapySequenceInitialStatus } from '../../util/patients-therapies.js';
import { isoDateFromTimestamp, timestampFromIsoDate } from '../../util/protocol-cycles.js';
import { EventLogs } from '../../utils.js';
import { addEventLog, EventLogResources } from '../util.js';
import { itemsDetail } from '../../util/event-log-items.js';
import { sequenceLogItems } from './medicine-log-items.js';
/**
 * Reverses a stop: the protocol becomes active again and its cancelled prescriptions from today
 * onwards go back to the default (not yet validated) status. Earlier cancelled prescriptions
 * stay cancelled, since their date has already passed.
 */
export async function reactivatePatientProtocol(tx, input, userId) {
    const now = Date.now();
    const today = timestampFromIsoDate(isoDateFromTimestamp(now));
    const patientProtocol = await tx.run(zql.patientProtocols
        .related('protocol')
        .where('id', '=', input.patientProtocolId)
        .where('deletedAt', 'IS', null)
        .one());
    if (!patientProtocol)
        throw new Error('Not Found');
    if (patientProtocol.endDate == null)
        throw new Error('The protocol is not stopped');
    const characteristics = await tx.run(zql.patientCharacteristics
        .related('patient')
        .where('patientId', '=', patientProtocol.patientId)
        .orderBy('createdAt', 'desc')
        .limit(1)
        .one());
    const sequences = await tx.run(zql.patientTherapySequence
        .where('patientProtocolId', '=', input.patientProtocolId)
        .where('status', '=', PatientTherapySequenceStatus.enum.cancelled)
        .where('deletedAt', 'IS', null));
    const defaultStatus = getTherapySequenceInitialStatus(false);
    const sequenceOps = [];
    const logOps = [];
    const sequencesToRestore = sequences.filter(s => s.therapyDate >= today);
    for (const seq of sequencesToRestore) {
        const reset = {
            status: defaultStatus,
            doctorValidatedBy: null,
            doctorValidatedAt: null,
            pharmacistValidatedAt: null,
            patientArrivedAt: null,
            updatedAt: now,
            updatedBy: userId,
        };
        sequenceOps.push(() => tx.mutate.patientTherapySequence.update({ id: seq.id, ...reset }));
        if (tx.location === 'server') {
            logOps.push(() => addEventLog(tx, {
                id: uuidv7(),
                resource: EventLogResources.patientTherapySequence,
                resourceId: seq.id,
                action: 'update',
                domain: 'prescription',
                event: EventLogs.PATIENT_THERAPY_SEQUENCE_PROTOCOL_UPDATED,
                patches: createPatch(seq, { ...seq, ...reset }),
                performedBy: userId,
                performedAt: now,
                details: {
                    therapyInternalId: seq.internalId,
                    patientId: patientProtocol.patientId,
                    patientName: characteristics?.patient?.fullName ?? '',
                    ...itemsDetail(sequenceLogItems(seq, reset)),
                },
            }));
        }
    }
    sequenceOps.push(() => tx.mutate.patientProtocols.update({
        id: input.patientProtocolId,
        endDate: null,
        updatedAt: now,
        updatedBy: userId,
    }));
    await Promise.all(sequenceOps.map(op => op()));
    await Promise.all(logOps.map(op => op()));
    if (tx.location === 'server') {
        await addEventLog(tx, {
            id: uuidv7(),
            resource: EventLogResources.patientProtocol,
            resourceId: input.patientProtocolId,
            action: 'update',
            domain: 'patient',
            event: EventLogs.PATIENT_PROTOCOL_REACTIVATED,
            patches: createPatch({ endDate: patientProtocol.endDate }, { endDate: null }),
            status: 'OK',
            performedBy: userId,
            performedAt: now,
            details: {
                patientId: patientProtocol.patientId,
                patientName: characteristics?.patient?.fullName ?? '',
                protocolName: patientProtocol.protocol?.name,
                restoredPrescriptions: String(sequencesToRestore.filter(seq => !seq.isNote).length),
            },
        });
    }
}
//# sourceMappingURL=reactivate-patient-protocol.js.map